import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Table, Tag, Button, Progress, Tooltip, Modal } from 'antd'
import { EyeOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import RunDetailsModal from './RunDetailsModal'
import styles from './Component.css'

export default class _RunsTable_ extends React.PureComponent {

  static propTypes = {
    runs: PropTypes.array.isRequired,
    pagination: PropTypes.object.isRequired,
    fetching: PropTypes.bool.isRequired,
    onChangeTable: PropTypes.func.isRequired,
    onViewRunDetails: PropTypes.func.isRequired,
    onRefreshRuns: PropTypes.func.isRequired,
    runDetails: PropTypes.object,
  }

  state = {
    selectedRun: null,
    showDetailsModal: false,
  }

  handleViewRun = (run) => {
    this.setState({
      selectedRun: run,
      showDetailsModal: true,
    })
    this.props.onViewRunDetails(run.id)
  }

  handleCloseModal = () => {
    this.setState({
      selectedRun: null,
      showDetailsModal: false,
    })
    // Refresh the runs list to show updated status
    this.props.onRefreshRuns()
  }

  handleRefreshRunDetails = () => {
    const { selectedRun } = this.state
    if (selectedRun && selectedRun.id) {
      this.props.onViewRunDetails(selectedRun.id)
    }
  }

  getStatusTag = (status) => {
    const statusConfig = {
      'pending': { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
      'running': { color: 'blue', icon: <ClockCircleOutlined />, text: 'Running' },
      'completed': { color: 'green', icon: <CheckCircleOutlined />, text: 'Distribution Complete' },
      'failed': { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Failed' },
      'fully_completed': { color: 'green', icon: <CheckCircleOutlined />, text: 'Fully Completed' },
      'partially_completed': { color: 'blue', icon: <CheckCircleOutlined />, text: 'Partially Completed' },
      'sending': { color: 'orange', icon: <ClockCircleOutlined />, text: 'Sending Emails' },
      'sent': { color: 'cyan', icon: <CheckCircleOutlined />, text: 'Emails Sent' },
      'pending_emails': { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending Emails' },
      'on_hold': { color: 'red', icon: <ClockCircleOutlined />, text: 'On Hold' },
    }

    const config = statusConfig[status] || statusConfig['pending']
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    )
  }

  getCompletionProgress = (record) => {
    const total = record.total_recipients || 0
    const signed = record.signed_count || 0
    const viewed = record.viewed_count || 0

    if (total === 0) return { percent: 0, status: 'normal' }

    const signedPercent = Math.round((signed / total) * 100)
    const viewedPercent = Math.round((viewed / total) * 100)

    let status = 'normal'
    if (signedPercent === 100) status = 'success'
    else if (signedPercent > 0) status = 'active'

    return { percent: signedPercent, status, viewedPercent }
  }

  render () {
    const { runs, pagination, fetching, onChangeTable, runDetails } = this.props
    const { selectedRun, showDetailsModal } = this.state
console.log("RUNS", runs);
    // Get detailed run data if available
    const selectedRunWithDetails = selectedRun && runDetails && runDetails[selectedRun.id]
      ? runDetails[selectedRun.id]
      : selectedRun

    const columns = [
      {
        title: 'Run ID',
        key: 'id',
        fixed: 'left',
        sorter: true,
        width: 100,
        render: (text, record) => `#${record.id}`,
      },
      {
        title: 'Status',
        key: 'status',
        fixed: 'left',
        sorter: true,
        width: 120,
        render: (text, record) => this.getStatusTag(record.actual_status || record.status),
      },
      {
        title: 'Created',
        key: 'created_at',
        width: 150,
        sorter: true,
        render: (text, record) => moment(record.created_at).format('DD MMM YYYY HH:mm'),
      },
      {
        title: 'Documents',
        key: 'documents_count',
        width: 120,
        sorter: true,
        render: (text, record) => (
          <Tooltip title={`${record.documents_used} document${record.documents_used !== 1 ? 's' : ''} distributed`}>
            <Tag color="blue">{record.documents_used}</Tag>
          </Tooltip>
        ),
      },
      {
        title: 'Recipients',
        key: 'total_recipients',
        width: 120,
        sorter: true,
        render: (text, record) => (
          <Tooltip title={`Sent to ${record.total_recipients} subcontractor${record.total_recipients !== 1 ? 's' : ''}`}>
            <Tag color="purple">{record.total_recipients}</Tag>
          </Tooltip>
        ),
      },
      {
        title: 'Progress',
        key: 'progress',
        width: 200,
        render: (text, record) => {
          const { percent, status, viewedPercent } = this.getCompletionProgress(record)
          return (
            <div style={{ width: '100%', maxWidth: '180px' }}>
              <Progress
                percent={percent}
                status={status}
                size="small"
                format={() => `${record.signed_count || 0}/${record.total_recipients || 0} signed`}
                style={{ width: '100%' }}
              />
              <br />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {record.viewed_count || 0} viewed
              </div>
            </div>
          )
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        width: 100,
        render: (text, record) => (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => this.handleViewRun(record)}
          >
            View
          </Button>
        ),
      },
    ]

    const scroll = { x: columns.reduce((total, column) => total + column.width, 0) }

    return (
      <div className={styles.wrapper}>
        <Table
          dataSource={runs}
          columns={columns}
          rowKey={record => record.id}
          loading={fetching}
          onChange={onChangeTable}
          scroll={scroll}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '40', '50'],
            ...pagination,
            style: { float: 'none', textAlign: 'center' },
          }}
        />

        <RunDetailsModal
          visible={showDetailsModal}
          run={selectedRunWithDetails}
          onClose={this.handleCloseModal}
          onRunUpdated={this.props.onRefreshRuns}
          onRefreshRunDetails={this.handleRefreshRunDetails}
        />
      </div>
    )
  }
}
